import React, { useState } from "react";
import cx from "classnames";

import ChevronDown from "../../assets/icons/chevron-down.svg";
import styles from "./chip.module.scss";
import IconContainer from "../IconContainer/IconContainer";
import { polymorphic, classMapper } from "../utils";
import Cross from "../../assets/icons/cross.svg";

const classMap = {
  size: {
    micro: styles.chipMicro,
    "extra-small": styles.chipExtraSmall,
    small: styles.chipSmall,
    medium: styles.chipMedium
  },
  color: {
    light: styles.chipLight,
    gray: styles.chipGray,
    "light-green": styles.chipLightGreen,
    "light-blue": styles.chipLightBlue,
    "deep-blue": styles.chipDeepBlue
  },
  displayMode: {
    inline: styles.chipInline,
    block: styles.chipBlock
  }
};

export namespace Chip {
  export type Props = {
    animated?: boolean;
    className?: string;
    label?: React.ReactNode;
    value?: string;
    icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    emoji?: string | null;
    deletable?: boolean;
    changeable?: boolean;
    pill?: boolean;
    popoverTrigger?: boolean;
    color?: keyof typeof classMap.color;
    size?: keyof typeof classMap.size;
    displayMode?: keyof typeof classMap.displayMode;
    onDelete?(): void;
    onClick?(): void;
  };
}

export const Chip = polymorphic("div")<Chip.Props>(
  ({
    animated = false,
    className,
    label,
    value,
    icon,
    deletable,
    pill = true,
    emoji = null,
    popoverTrigger = false,
    color = "light",
    size = "medium",
    displayMode = "inline",
    onClick,
    onDelete,
    ...props
  }: Chip.Props): JSX.Element => {
    const [deleted, setDeleted] = useState(false);
    const classList = cx(
      className,
      styles.chip,
      onClick && styles.chipClickable,
      deleted && styles.chipDeleted,
      pill && styles.chipPill,
      ...classMapper(classMap, {
        size,
        displayMode,
        color
      })
    );

    const handleDelete = () => {
      if (!animated) {
        onDelete?.();
        return;
      }

      setDeleted(true);
      setTimeout(() => onDelete?.(), 1000);
    };

    return (
      <div {...props} className={classList}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <span
          className={styles.chipItem}
          onClick={onClick}
          onKeyPress={(event: React.KeyboardEvent<HTMLSpanElement>): void => {
            if (onClick && event.key === "Space") {
              onClick();
            }
          }}
        >
          {icon && size !== "micro" && (
            <span className={styles.chipItemIcon}>
              <IconContainer icon={icon} size={size === "small" ? 1 : 2} />
            </span>
          )}
          {emoji && size !== "micro" && (
            <span className={styles.chipItemEmoji} role="img">
              {emoji}
            </span>
          )}
          {label && size !== "micro" && (
            <span className={styles.chipItemLabel}>{label}</span>
          )}
          {value && <span className={styles.chipItemValue}>{value}</span>}
          {popoverTrigger && size !== "micro" && (
            <span className={styles.chipItemPopoverTrigger}>
              <IconContainer icon={ChevronDown} size={4} strokeWidth="thin" />
            </span>
          )}
        </span>
        {(onDelete || deletable) && size !== "micro" && (
          <span
            className={styles.chipDeletable}
            onClick={handleDelete}
            onKeyPress={(event: React.KeyboardEvent<HTMLSpanElement>): void => {
              if (event.key === "Space") {
                handleDelete();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span className={styles.chipDeletableContainer}>
              <IconContainer icon={Cross} size={1} />
            </span>
          </span>
        )}
      </div>
    );
  }
);
